import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { ScriptureAnchor } from '@/types/prayer';
import { prayerEditorAuth } from '@/lib/prayer-authoring';
import { prayScriptureDraft } from '@/lib/pray-scripture-draft-store';
import { getAudioQueueRow } from '@/lib/db';
import { useSettingsStore } from '@/lib/settings-store';
import { useAudioQueueStore } from '@/lib/audio-queue-store';
import { revisePrayerAttempt } from '@/lib/content-lifecycle/store';
import { getCurrentContentLifecycleContext, syncContentLifecycle } from '@/lib/content-lifecycle/sync';
import { isCoreReadOnly } from '@/lib/core-cutover';
import {
  PENDING_PRAYER_ALREADY_SAVED, collectInlineAudioCommitClaims, createPendingPrayerFromAudio,
  finalizeCommittedAudio, findHeldRecordingConflicts, getInlineAudioDraftRecovery,
  inlineSaveOfferFailedWithoutWords, newestInlineSaveOffer, publishCommittedInlineAudio,
  refreshInlineAudioMirror, saveRefusedForUnreadableRecording,
} from '@/lib/audio-queue';
import { useVoiceCapture } from './useVoiceCapture';

export function usePrayScriptureDraft(anchor: ScriptureAnchor) {
  const [auth] = useState(prayerEditorAuth);
  const defaultPrivate = useSettingsStore(s => s.defaultPrivate);
  const draft = useMemo(() => prayScriptureDraft(auth, anchor, defaultPrivate), [auth, anchor, defaultPrivate]);
  const state = useSyncExternalStore(draft.subscribe, draft.getSnapshot, draft.getSnapshot);
  const ready = draft.isCurrent() && state.loading === 'ready' && !isCoreReadOnly();
  const voice = useVoiceCapture();
  const registerTarget = voice.registerTarget;
  const stopVoice = voice.stop;
  const cancelPendingStart = voice.cancelPendingStart;
  const getCaptureState = voice.getCaptureState;
  const items = useAudioQueueStore(s => s.items);
  const isTarget = voice.target === draft.voiceTarget;
  const isListening = isTarget && voice.isRecording;
  const isTranscribing = isTarget && voice.isTranscribing;
  const interim = isTarget ? voice.interim : '';
  const text = draft.isCurrent() ? state.text : '';
  const displayValue = interim ? (text ? `${text} ${interim}` : interim) : text;
  const lastInterim = useRef('');
  const startRequest = useRef<{ target: string; token: string | null } | null>(null);
  const stopRef = useRef<{ token: string; promise: Promise<void> } | null>(null);
  const [recoveryError, setRecoveryError] = useState(false);
  const [recordingWords, setRecordingWords] = useState<Array<{ id: string; text: string }>>([]);

  // The helper checks owner/source; the following authoritative row read also
  // checks the frozen passage. Recheck captured authority after every await.
  const readRecording = useCallback(async (id: string) => {
    if (!draft.isCurrent() || !draft.knownIds.has(id)) return null;
    const recovered = await getInlineAudioDraftRecovery(id, 'prayer');
    if (!draft.isCurrent()) return null;
    const row = await getAudioQueueRow(id);
    if (!draft.isCurrent() || !recovered) return null;
    if (!row) return recovered.rowMissing ? recovered : null;
    if (row.ownerId !== draft.auth.ownerId || (row.inlineSaveSource ?? 'prayer') !== 'prayer' ||
      row.recoveryKind !== 'new-prayer' || !draft.matchesAnchor(row.scriptureAnchor) ||
      (recovered.heldWords ?? '') !== (row.transcribedText?.trim() ?? '')) return null;
    return recovered;
  }, [draft]);

  useLayoutEffect(() => { void draft.load(true); }, [draft]);
  useLayoutEffect(() => { lastInterim.current = interim; }, [interim]);
  useLayoutEffect(() => () => { draft.receive(lastInterim.current); }, [draft]);
  useEffect(() => {
    if (isCoreReadOnly()) return;
    const registration = ++draft.targetRegistration;
    // This registration owns this object. Its cleanup never reads the next
    // registration's request, including when the same draft reopens.
    const request = { target: draft.voiceTarget, token: null as string | null };
    startRequest.current = request;
    let mounted = true;
    let detached = false;
    const detach = () => {
      if (detached) return;
      detached = true;
      // A reopened editor owns its newer registration under the same target.
      if (draft.targetRegistration === registration) unregister();
    };
    const unregister = registerTarget(draft.voiceTarget, {
      onFinal: (words, id) => {
        if (!id) { draft.receive(words); return; }
        // Capture durability has already associated this exact recording.
        void readRecording(id).then(recovered => {
          if (recovered?.heldWords && draft.activeIds.has(id) && !draft.excludedIds.has(id)) draft.heldWords.set(id, recovered.heldWords);
          if (recovered?.transcribedText) draft.receive(recovered.transcribedText, id);
        }).catch(() => { draft.patch({ noWords: true }); });
      },
      getRecoveryText: () => draft.getRecoveryText(),
      onCaptureDurable: notice => {
        draft.captureDurable(notice);
        if (!mounted && draft.durableTokens.has(notice.captureToken ?? '')) queueMicrotask(detach);
      },
    });
    return () => {
      mounted = false;
      if (request.token) cancelPendingStart(request.target, request.token);
      draft.receive(lastInterim.current);
      const capture = getCaptureState();
      const token = capture.captureToken;
      if (capture.target !== draft.voiceTarget || !token || !draft.captureTokens.has(token) || draft.durableTokens.has(token)) {
        detach(); return;
      }
      // Forced route teardown cannot keep the sheet mounted. Keep this exact
      // callback registered until custody or the empty-stop settlement instead.
      draft.patch({ capture: 'stopping' });
      void stopVoice().then(() => {
        if (draft.activeCaptureToken === token) draft.patch({ capture: 'idle' });
        detach();
      }, () => {
        if (draft.activeCaptureToken === token) draft.patch({ capture: 'failed' });
        // Keep the association for a late durable notice; a new mount replaces
        // it, and provider/owner teardown retains its existing authority.
      });
    };
  }, [draft, readRecording, registerTarget, getCaptureState, stopVoice, cancelPendingStart]);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const available: Array<{ id: string; text: string }> = [];
      for (const id of draft.knownIds) {
        if (!draft.activeIds.has(id) && !draft.excludedIds.has(id)) continue;
        const recovered = await readRecording(id);
        if (cancelled || !draft.isCurrent()) return;
        if (recovered?.heldWords && draft.activeIds.has(id) && !draft.excludedIds.has(id)) draft.heldWords.set(id, recovered.heldWords);
        if (recovered?.transcribedText) {
          if (draft.excludedIds.has(id)) available.push({ id, text: recovered.transcribedText });
          else draft.receive(recovered.transcribedText, id);
        }
      }
      if (!cancelled) { setRecordingWords(available); setRecoveryError(false); }
    })().catch(() => { if (!cancelled) setRecoveryError(true); });
    return () => { cancelled = true; };
  }, [draft, readRecording, items, state.audioRevision, state.loading, state.revision]);

  const wasCapturing = useRef(false);
  useEffect(() => {
    if (isListening || isTranscribing) { wasCapturing.current = true; return; }
    if (wasCapturing.current) {
      wasCapturing.current = false;
      if (!draft.getSnapshot().text.trim()) draft.patch({ noWords: true });
    }
  }, [draft, isListening, isTranscribing]);

  const offer = newestInlineSaveOffer(items.filter(item => draft.knownIds.has(item.id) && !draft.excludedIds.has(item.id)), 'prayer', draft.anchor);
  const stopCapture = useCallback(() => {
    const capture = voice.getCaptureState();
    if (!draft.isCurrent() || capture.target !== draft.voiceTarget || !capture.captureToken || !draft.captureTokens.has(capture.captureToken)) return null;
    draft.receive(lastInterim.current);
    if (stopRef.current?.token === capture.captureToken) return stopRef.current.promise;
    const token = capture.captureToken;
    draft.patch({ capture: draft.durableTokens.has(token) ? 'idle' : 'stopping' });
    const promise = voice.stop().then(() => {
      if (draft.activeCaptureToken === token) draft.patch({ capture: 'idle', noWords: !draft.getSnapshot().text.trim() });
    }, () => { if (draft.activeCaptureToken === token) draft.patch({ capture: 'failed' }); });
    stopRef.current = { token, promise };
    return promise;
  }, [draft, voice]);
  const keepDraft = useCallback(() => {
    draft.receive(lastInterim.current);
    if (draft.getSnapshot().operation) return false;
    const capture = voice.getCaptureState();
    if (capture.target === draft.voiceTarget && capture.captureToken && draft.captureTokens.has(capture.captureToken)) {
      if (draft.durableTokens.has(capture.captureToken)) return true;
      void stopCapture(); return false;
    }
    // A waiting disclosure is not an accepted capture. Dismiss only this
    // registration's exact request; ordinary active capture still stops above.
    const request = startRequest.current;
    if (request?.target === draft.voiceTarget && request.token) cancelPendingStart(request.target, request.token);
    return true;
  }, [draft, voice, stopCapture, cancelPendingStart]);
  const toggleMic = useCallback(() => {
    if (!draft.isCurrent() || isCoreReadOnly() || draft.getSnapshot().loading !== 'ready' || draft.getSnapshot().operation) return;
    const capture = voice.getCaptureState();
    if (capture.target === draft.voiceTarget) { void stopCapture(); return; }
    const token = crypto.randomUUID();
    const request = startRequest.current;
    if (request?.target !== draft.voiceTarget) return;
    request.token = token;
    draft.captureTokens.add(token); draft.activeCaptureToken = token; draft.patch({ noWords: false, capture: 'idle' });
    if (!voice.start(draft.voiceTarget, { scriptureAnchor: draft.anchor, captureToken: token })) draft.captureTokens.delete(token);
  }, [draft, voice, stopCapture]);
  const handleChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!ready || state.operation) return;
    if (isListening || interim) void stopCapture();
    draft.setText(event.target.value);
  }, [draft, ready, state.operation, isListening, interim, stopCapture]);

  const save = useCallback(async () => {
    if (!draft.isCurrent() || isCoreReadOnly() || isListening || isTranscribing || draft.getSnapshot().capture === 'stopping') return false;
    const captured = draft.begin('save');
    if (!captured) return false;
    let committed = false;
    const excluded = new Set(captured.ids);
    try {
      await draft.load(true);
      if (!draft.isCurrent() || draft.getSnapshot().loading !== 'ready' || draft.getSnapshot().resolution !== 'none' || draft.session !== captured.session) return false;
      const trimmed = captured.text.trim();
      if (!trimmed) {
        const pending = newestInlineSaveOffer(useAudioQueueStore.getState().items.filter(item => captured.ids.has(item.id)), 'prayer', draft.anchor);
        if (!pending || inlineSaveOfferFailedWithoutWords(pending)) { draft.patch({ saveError: true }); return false; }
        const row = await getAudioQueueRow(pending.id);
        if (!draft.isCurrent() || !row || row.ownerId !== draft.auth.ownerId || !draft.matchesAnchor(row.scriptureAnchor) ||
          (row.inlineSaveSource ?? 'prayer') !== 'prayer' || (row.registryState === 'failed' && !row.transcribedText?.trim())) { draft.patch({ saveError: true }); return false; }
        await createPendingPrayerFromAudio({ ...captured.candidate, rawText: '', pendingTranscriptionId: pending.id }, pending.id);
        committed = true; return true;
      }
      // Validate known recording anchors without replacing missing IDs: a
      // missing held recording must still yield the existing unknown outcome.
      for (const id of captured.ids) {
        const row = await getAudioQueueRow(id);
        if (!draft.isCurrent()) return false;
        if (row && (row.ownerId !== draft.auth.ownerId || !draft.matchesAnchor(row.scriptureAnchor) || (row.inlineSaveSource ?? 'prayer') !== 'prayer')) throw new Error('scripture-recording-mismatch');
      }
      const conflicts = await findHeldRecordingConflicts(captured.heldWords, trimmed, draft.anchor);
      if (!draft.isCurrent()) return false;
      if (conflicts.saved.length + conflicts.unknown.length) {
        draft.patch({ heldConflict: { kind: conflicts.unknown.length ? 'unknown' : 'saved', ids: [...conflicts.saved, ...conflicts.unknown] } }); return false;
      }
      const claims = await collectInlineAudioCommitClaims(captured.ids, 'prayer', trimmed);
      if (!draft.isCurrent()) return false;
      const prayer = { ...captured.candidate, rawText: trimmed };
      draft.candidate = prayer;
      const intent = { kind: 'create' as const, prayer };
      let result;
      try { result = await captured.session.save(prayer.id, intent, claims); }
      finally { draft.rememberSubmittedAttempt(captured.session, captured.revision, intent, claims, captured.ids); }
      if (result.kind !== 'committed') { await draft.load(true); draft.patch({ saveError: true }); return false; }
      committed = true;
      for (const claim of result.consumedAudio) excluded.delete(claim.queueItemId);
      if (draft.isCurrent() && result.consumedAudio.length) {
        void publishCommittedInlineAudio(result.consumedAudio.map(c => c.queueItemId));
        void syncContentLifecycle().then(async () => {
          for (const claim of result.consumedAudio) await finalizeCommittedAudio(claim.queueItemId).catch(() => false);
        }).catch(() => {});
      }
      return true;
    } catch (error) {
      if (error instanceof Error && error.message === PENDING_PRAYER_ALREADY_SAVED) { committed = true; return true; }
      draft.patch({ saveError: true, saveReopen: saveRefusedForUnreadableRecording(error) ? (captured.text.trim() ? 'words' : 'recording') : null });
      if (draft.isCurrent()) void refreshInlineAudioMirror([...captured.ids]);
      return false;
    } finally { draft.finish(captured.revision, committed, excluded, committed, captured.ids); }
  }, [draft, isListening, isTranscribing]);
  const discard = useCallback(async (revision: number) => {
    if (isCoreReadOnly() || draft.getSnapshot().revision !== revision) { draft.patch({ discardError: true }); return false; }
    const captured = draft.begin('discard'); if (!captured) return false;
    let discarded = false;
    try {
      await draft.load(true);
      if (!draft.isCurrent() || draft.getSnapshot().loading !== 'ready' || draft.getSnapshot().resolution === 'changed' || draft.session !== captured.session) { draft.patch({ discardError: true }); return false; }
      if (captured.session.attempt && draft.getSnapshot().resolution !== 'unknown') {
        const context = await getCurrentContentLifecycleContext(draft.auth);
        await revisePrayerAttempt(context, captured.session.attempt, null);
      }
      if (!draft.isCurrent()) return false;
      discarded = true; return true;
    } catch { draft.patch({ discardError: true }); return false; }
    finally { draft.finish(captured.revision, discarded, captured.ids); }
  }, [draft]);
  const saveSeparate = useCallback(async () => {
    if (draft.getSnapshot().resolution === 'saved' && !draft.chooseSuccessor()) return false;
    draft.releaseConflictingIds(draft.getSnapshot().heldConflict?.ids ?? []);
    return save();
  }, [draft, save]);
  const useRecordingWords = useCallback(() => {
    for (const item of recordingWords) draft.useRecordingWords(item.text, item.id);
  }, [draft, recordingWords]);
  return { draft, state, ready, displayValue, isListening, isTranscribing, isSupported: voice.isSupported,
    offer, recoveryError, recordingWords, useRecordingWords, toggleMic, handleChange, save, saveSeparate, discard, keepDraft,
    stopCapture, retryLoad: () => { void draft.load(true); },
    canSave: ready && !state.operation && state.resolution === 'none' && !state.heldConflict &&
      (Boolean(text.trim()) || Boolean(offer && !inlineSaveOfferFailedWithoutWords(offer))),
  };
}
